/**
 * LOGIN.MODAL
 * Connexion to the app
 */

import env from "@/env"
import cookie from "react-cookies"
import { withTranslation, WithTranslation } from "react-i18next"
import LoadingModal from "./loading.modal"
import { useEffect, useRef, useState } from "react"
import { store } from "@/index"
import { firebaseAppAuth, firebaseProvider, instanceBackend } from "@/App"
import { _authEmail, _authPassword, _authSsoAuthenticate, _authSupervisor } from "@/redux/_auth.actions"
import { accountConnect, accountConnect_AsSupervisor } from "@/redux/account.actions"
import handleLogin from "@/utils/handle-login.utils"
import { INTERFACE_TYPE } from "@/redux/_session.types"
import i18n from "@/translate/i18n"
import { toast } from "react-toastify"
import { NavigateFunction, useNavigate } from "react-router-dom"
import { signInWithEmailAndPassword, signInWithPopup, signOut } from "firebase/auth"
import { projectActivate } from "@/redux/project.actions"
import { Project } from "@/redux/project.types"
import Modal from "@/components/modal"
import GetSurveyResultsModal from "./get-survey-results.modal"
import { ACCOUNT_DEMO } from "@/redux/account.types"

interface OwnProps {
  accountIdForSuperadmin?: string //Id of the account to connect as superamdin
  authLevel: number //0 = public, 1 = email, 2 = password
  code?: string //Code for supervisor
  email?: string //Email (optional)
  isSupervisor?: boolean //Connect as supervisor user (idtree customer success)
  password?: string //Password (optional)
  onClose?: Function //Trigger fonction on close
  toSupervisor?: boolean //Login to supervisor interface
  sso?: boolean //Auth with SSO
  workspaceIdentity?: { email: string; firebaseToken: string }
  withGoogle?: boolean //Login with google
}

type Props = OwnProps & WithTranslation

function LoginModal(props: Props) {
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  const [errorMessage, setErrorMessage] = useState<null | string>(null)
  const [tasksRedirection, setTasksRedirection] = useState<null | string>(null)

  const started = useRef(false)
  useEffect(() => {
    if (started.current) return
    started.current = true
    //Auth SSO
    async function authWithSSO() {
      const queryParams: URLSearchParams = new URLSearchParams(window.location.search)
      const code: string | null = queryParams.get("code")
      if (!code) {
        return "no_auth_code"
      } else {
        return await store.dispatch(_authSsoAuthenticate(code))
      }
    }

    //Auth with firebase
    async function authWithFirebase(email, password) {
      try {
        const firebaseResponse: any = await signInWithEmailAndPassword(firebaseAppAuth, email, password)
        const firebaseToken = await firebaseResponse.user.getIdToken()
        cookie.save("firebaseToken", firebaseToken, { path: "/" })
        instanceBackend.defaults.headers.common["firebaseToken"] = firebaseToken
        return null
      } catch (error: any) {
        return t("firebase_" + error.code)
      }
    }

    //Auth with google
    async function authWithGoogle() {
      try {
        const googleResponse: any = await signInWithPopup(firebaseAppAuth, firebaseProvider)
        if (firebaseAppAuth.currentUser) {
          const firebaseToken = await firebaseAppAuth.currentUser.getIdToken()
          cookie.save("firebaseToken", firebaseToken, { path: "/" })
          instanceBackend.defaults.headers.common["firebaseToken"] = firebaseToken
        }
        return googleResponse.user.email
      } catch (error: any) {
        return { error: t("firebase_" + error.code) }
      }
    }

    async function login() {
      //Init error of connexion
      let error: string | null = null

      //Clean stores
      store.dispatch(projectActivate(new Project()))

      //Response of the idtree backend
      let response: any = null
      let email = props.email

      //Get URL params
      const searchParams = new URLSearchParams(window.location.search)
      const id = searchParams.get("id") //Login with user id (for observer)
      const isAdmin = searchParams.get("isAdmin") //Connected as admin (hide)
      const lg = searchParams.get("lg") //Specify language

      //If email and password provided auth with firebase
      if (props.authLevel === 2) {
        if (props.workspaceIdentity) {
          instanceBackend.defaults.headers.common["firebaseToken"] = props.workspaceIdentity.firebaseToken
          email = props.workspaceIdentity.email
        }
        //Signup with google
        else if (props.withGoogle) {
          const googleResponse = await authWithGoogle()
          if (googleResponse.error) {
            error = googleResponse.error
          } else {
            email = googleResponse
          }
        }
        //Signup login/password
        else if (email && props.password) {
          error = await authWithFirebase(email, props.password)
          if (error) {
            toast(error, { type: "error" })
          }
        }

        //Specific case for the SSo
        if (props.sso) {
          response = await authWithSSO()
        }
        //If firebase auth is valid auth to backend
        else if (!error) {
          if (props.toSupervisor) {
            response = await store.dispatch(_authSupervisor())
          } else if (props.accountIdForSuperadmin) {
            const connectFunction =
              store.getState()._session.interfaceType === "SUPERVISOR" ? accountConnect_AsSupervisor : accountConnect
            response = await store.dispatch(
              connectFunction(
                props.accountIdForSuperadmin,
                email,
                props.accountIdForSuperadmin === ACCOUNT_DEMO.id ? store.getState()._session.accountDemoSurvey : null
              )
            )
          } else {
            response = await store.dispatch(_authPassword(email))
          }
        }
      }
      //Auth with user id
      else if (props.authLevel === 1) {
        if (!id) {
          error = t("utils_token_empty")
        } else {
          response = await store.dispatch(_authEmail(id, isAdmin))
          if (response.ssoOrganization) {
            window.location.href = window.location.origin + "/login/sso/auth/" + response.ssoOrganization
          }
          response.asAdmin = isAdmin
        }
      }

      //If no response was provided
      if (!response) {
        response = {
          user: {
            level: 2
          },
          account: {}
        }
      }

      //SSO user without access to the app: straight to the survey in progress
      if (props.sso && response.qvCode && !response.token) {
        window.location.href = env.REACT_APP_URL_QUICKVOTE + "/" + response.qvCode
        return
      }

      //Handle response
      if (response.error) {
        error = t(response.error)
      }

      //Set error message
      setErrorMessage(error)
      if (error && props.toSupervisor) {
        await signOut(firebaseAppAuth)
        delete instanceBackend.defaults.headers.common["firebaseToken"]
        cookie.remove("firebaseToken", { path: "/" })
      }

      //End of login if no error
      if (response.redirection) {
        i18n.changeLanguage(response.language === "fr" ? "fr" : "en")
        navigate(response.redirection)
      } else if (!error) {
        let interfaceType: INTERFACE_TYPE = "ADMIN"
        if (props.toSupervisor) interfaceType = "SUPERVISOR"

        const redirection = await handleLogin(
          response,
          interfaceType,
          props.isSupervisor ? true : false,
          props.authLevel,
          searchParams,
          lg
        )

        if (redirection) {
          if (redirection.includes("/tasks")) {
            setTasksRedirection(redirection)
          } else {
            navigate(redirection)
          }
        }
      }

      if (props.onClose && !error) {
        props.onClose()
      }
    }

    login()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (tasksRedirection)
    return <GetSurveyResultsModal onClose={() => setTasksRedirection(null)} destination={tasksRedirection} />

  return errorMessage ? (
    <Modal
      onClose={() =>
        props.onClose ? props.onClose() : navigate(props.toSupervisor ? "/login/supervisor" : "/login/app")
      }
      isCloseButtonVisible
      disableClickOutside>
      <b>{errorMessage}</b>
    </Modal>
  ) : (
    <LoadingModal />
  )
}

export default withTranslation()(LoginModal)
